/**
 * Component Showcase
 * Interactive catalog of all TrustGrid framework components
 * Theme editor and component library reference
 */

import React, { useState } from 'react';
import {
  Tabs,
  Row,
  Col,
  Card,
  Button,
  Input,
  Space,
  Divider,
  Typography,
  Tag,
  Alert,
  ColorPicker,
  Slider,
  Switch,
  Form,
  Select,
} from 'antd';
import {
  DashboardOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  FileSearchOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  DownloadOutlined,
  ReloadOutlined,
  CodeOutlined,
} from '@ant-design/icons';

// Layout components
import {
  TrustGridPage,
  Section,
  EmptyState,
  PageHeader,
} from './layout';

// Common components
import {
  StatusTag,
  SeverityTag,
  ScoreDisplay,
  ScoreGauge,
  DataCard,
  TrustGridTable,
  TGDataTable,
  ConfirmModal,
  FormModal,
  FilterBar,
  SearchInput,
  DateRangePicker,
  StatusSelect,
  MultiSelect,
  LoadingState,
  ErrorState,
  NoData,
  ProgressBar,
  TrendChart,
  InfoTooltip,
  Breadcrumbs,
  FormSection,
  toast,
} from './common';

// Hooks
import { useModal, useTable, useFilters } from '../hooks';

// Tab components
import DataGridTab from './ComponentShowcase_DataGrid';

// Config System
import GenericConfigPanel from '../modules/trustgrid/config/GenericConfigPanel';
import {
  CTRL_01_SCHEMA,
  CTRL_02_SCHEMA,
  CTRL_03_SCHEMA,
  CTRL_04_SCHEMA,
  CTRL_05_SCHEMA,
  CTRL_06_SCHEMA,
  CTRL_07_SCHEMA,
  CTRL_08_SCHEMA,
  CTRL_09_SCHEMA,
  CTRL_10_SCHEMA,
  CTRL_11_SCHEMA,
  CTRL_12_SCHEMA,
  CTRL_13_SCHEMA,
  CTRL_14_SCHEMA,
  getControlSchema,
} from '../modules/trustgrid/config/controlSchemas';

// Theme
import {
  colors,
  spacing,
  typography,
  shadows,
  borderRadius,
} from '../theme/designTokens';

const { Title, Text, Paragraph } = Typography;

/**
 * Theme Tokens Section
 */
function ThemeTokensTab() {
  return (
    <div>
      <Section title="Colors">
        <Title level={5}>Status Colors</Title>
        <Row gutter={[16, 16]}>
          {Object.entries(colors.status).map(([key, value]) => (
            <Col key={key} xs={24} sm={12} md={8}>
              <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md }}>
                <div
                  style={{
                    width: 60,
                    height: 60,
                    backgroundColor: value,
                    borderRadius: borderRadius.md,
                    border: `1px solid ${colors.border.light}`,
                  }}
                />
                <div>
                  <Text strong>{key}</Text>
                  <br />
                  <Text code style={{ fontSize: typography.fontSize.xs }}>
                    {value}
                  </Text>
                </div>
              </div>
            </Col>
          ))}
        </Row>

        <Divider />

        <Title level={5}>Severity Colors</Title>
        <Row gutter={[16, 16]}>
          {Object.entries(colors.severity).map(([key, config]) => (
            <Col key={key} xs={24} sm={12} md={8}>
              <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md }}>
                <div
                  style={{
                    width: 60,
                    height: 60,
                    backgroundColor: config.bg,
                    border: `2px solid ${config.main}`,
                    borderRadius: borderRadius.md,
                  }}
                />
                <div>
                  <Text strong>{key}</Text>
                  <br />
                  <Text code style={{ fontSize: typography.fontSize.xs }}>
                    {config.main}
                  </Text>
                </div>
              </div>
            </Col>
          ))}
        </Row>
      </Section>

      <Section title="Spacing Scale">
        <Space direction="vertical" style={{ width: '100%' }}>
          {Object.entries(spacing).map(([key, value]) => (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: spacing.md }}>
              <Text style={{ width: 80 }} strong>
                {key}
              </Text>
              <div
                style={{
                  height: 20,
                  width: value,
                  backgroundColor: colors.primary[500],
                  borderRadius: borderRadius.sm,
                }}
              />
              <Text code>{value}px</Text>
            </div>
          ))}
        </Space>
      </Section>

      <Section title="Typography">
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Title level={1}>
              Heading 1 (Inter)
            </Title>
            <Text code>fontFamily: Inter, fontSize: {typography.headings.h1.fontSize}px</Text>
          </div>
          <div>
            <Title level={2}>Heading 2 (Inter)</Title>
            <Text code>fontFamily: Inter, fontSize: {typography.headings.h2.fontSize}px</Text>
          </div>
          <div>
            <Title level={3}>Heading 3 (Inter)</Title>
            <Text code>fontFamily: Inter, fontSize: {typography.headings.h3.fontSize}px</Text>
          </div>
          <div>
            <Title level={4}>Heading 4 (Inter)</Title>
            <Text code>fontSize: {typography.headings.h4.fontSize}px</Text>
          </div>
          <div>
            <Title level={5}>Heading 5 (Inter)</Title>
            <Text code>fontSize: {typography.headings.h5.fontSize}px</Text>
          </div>
          <Divider />
          <Paragraph>
            Body text (Inter): Lorem ipsum dolor sit amet, consectetur adipiscing elit. This is the default UI font.
            <br />
            <Text code>fontFamily: Inter, fontSize: {typography.fontSize.base}px</Text>
          </Paragraph>
          <Paragraph style={{ fontSize: typography.fontSize.xs }}>
            Small text: Sed do eiusmod tempor incididunt ut labore.
            <br />
            <Text code>fontSize: {typography.fontSize.xs}px</Text>
          </Paragraph>
        </Space>
      </Section>
    </div>
  );
}

/**
 * Layout Components Tab
 */
function LayoutComponentsTab() {
  return (
    <div>
      <Section title="Section Component Variants">
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Section title="Default Section" variant="default">
            <Paragraph>Default section with shadow</Paragraph>
          </Section>

          <Section title="Bordered Section" variant="bordered">
            <Paragraph>Bordered section without shadow</Paragraph>
          </Section>

          <Section title="Elevated Section" variant="elevated">
            <Paragraph>Elevated section with stronger shadow</Paragraph>
          </Section>

          <Section title="Compact Section" variant="compact">
            <Paragraph>Compact section with less padding</Paragraph>
          </Section>

          <Section
            title="Collapsible Section"
            collapsible
            defaultCollapsed={false}
          >
            <Paragraph>Click the title to collapse/expand</Paragraph>
          </Section>
        </Space>
      </Section>

      <Section title="Empty States">
        <EmptyState
          icon={<FileSearchOutlined />}
          title="No Results Found"
          description="Try adjusting your search or filter criteria"
          actionText="Clear Filters"
          onAction={() => toast.success('Filters cleared!')}
        />
      </Section>

      <Section title="Page Header">
        <PageHeader
          title="Example Page"
          subtitle="This is a subtitle describing the page"
          breadcrumbs={[
            { label: 'TrustGrid', path: '/trustgrid' },
            { label: 'Projects' },
          ]}
          actions={
            <Space>
              <Button icon={<DownloadOutlined />}>Export</Button>
              <Button type="primary" icon={<PlusOutlined />}>
                Create
              </Button>
            </Space>
          }
        />
      </Section>
    </div>
  );
}

/**
 * Tags & Badges Tab
 */
function TagsTab() {
  return (
    <div>
      <Section title="Status Tags">
        <Space wrap>
          <StatusTag status="pass" />
          <StatusTag status="fail" />
          <StatusTag status="warning" />
          <StatusTag status="pending" />
          <StatusTag status="running" />
          <StatusTag status="unknown" />
        </Space>
        <Divider />
        <Title level={5}>With Icons</Title>
        <Space wrap>
          <StatusTag status="pass" showIcon />
          <StatusTag status="fail" showIcon />
          <StatusTag status="warning" showIcon />
          <StatusTag status="running" showIcon />
        </Space>
      </Section>

      <Section title="Severity Tags">
        <Space wrap>
          <SeverityTag severity="critical" />
          <SeverityTag severity="high" />
          <SeverityTag severity="medium" />
          <SeverityTag severity="low" />
        </Space>
      </Section>
    </div>
  );
}

/**
 * Scores Tab
 */
function ScoresTab() {
  const [score, setScore] = useState(75);

  return (
    <div>
      <Section
        title="Score Display Types"
        extra={
          <Space>
            <Text>Test Score:</Text>
            <Slider
              value={score}
              onChange={setScore}
              style={{ width: 200 }}
              tooltip={{ formatter: (val) => `${val}%` }}
            />
          </Space>
        }
      >
        <Row gutter={[24, 24]}>
          <Col xs={24} sm={8}>
            <ScoreDisplay score={score} type="gauge" size="large" label="Large Gauge" />
          </Col>
          <Col xs={24} sm={8}>
            <ScoreDisplay score={score} type="gauge" size="medium" label="Medium Gauge" />
          </Col>
          <Col xs={24} sm={8}>
            <ScoreDisplay score={score} type="gauge" size="small" label="Small Gauge" />
          </Col>
        </Row>

        <Divider />

        <Space direction="vertical" style={{ width: '100%' }}>
          <ScoreDisplay
            score={score}
            type="progress"
            label="Trust Score"
            tooltip="Overall trust score based on all controls"
          />
          <ScoreDisplay
            score={score}
            type="progress"
            label="Compliance Score"
          />
          <ScoreDisplay score={score} type="progress" label="Maturity Score" />
        </Space>

        <Divider />

        <Space>
          <Text>Text Display:</Text>
          <ScoreDisplay score={score} type="text" />
        </Space>
      </Section>
    </div>
  );
}

/**
 * Cards Tab
 */
function CardsTab() {
  return (
    <div>
      <Section title="Data Cards">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <DataCard
              title="Total Findings"
              value={142}
              icon={<WarningOutlined />}
              trend={-12}
              trendLabel="vs last week"
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <DataCard
              title="Critical Issues"
              value={8}
              icon={<CheckCircleOutlined />}
              trend={5}
              trendLabel="new this week"
              valueColor={colors.status.fail}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <DataCard
              title="Resolved"
              value={134}
              icon={<DashboardOutlined />}
              trend={15}
              trendLabel="this month"
              valueColor={colors.status.pass}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <DataCard
              title="Active Scans"
              value={3}
              icon={<FileSearchOutlined />}
            />
          </Col>
        </Row>
      </Section>
    </div>
  );
}

/**
 * Tables Tab
 */
function TablesTab() {
  const table = useTable({ defaultPageSize: 5 });

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Status', dataIndex: 'status', key: 'status', render: (status) => <StatusTag status={status} /> },
    { title: 'Severity', dataIndex: 'severity', key: 'severity', render: (sev) => <SeverityTag severity={sev} /> },
    { title: 'Score', dataIndex: 'score', key: 'score', render: (score) => <ScoreDisplay score={score} type="text" /> },
  ];

  const data = React.useMemo(() =>
    Array.from({ length: 12 }, (_, i) => ({
      id: i + 1,
      key: i + 1,
      name: `Item ${i + 1}`,
      status: ['pass', 'fail', 'warning'][i % 3],
      severity: ['critical', 'high', 'medium', 'low'][i % 4],
      score: 50 + (i * 5),
    }))
  , []);

  React.useEffect(() => {
    table.setTotal(data.length);
  }, [data.length, table]);

  return (
    <div>
      <Section title="TrustGrid Table">
        <TrustGridTable
          columns={columns}
          dataSource={data}
          tableProps={table.tableProps}
          showExport
          showColumnCustomization
          showRefresh
          onRefresh={() => toast.info('Refreshing...')}
          exportFilename="example.csv"
        />
      </Section>
    </div>
  );
}

/**
 * Modals Tab
 */
function ModalsTab() {
  const confirmModal = useModal();
  const formModal = useModal();

  return (
    <div>
      <Section title="Modal Components">
        <Space>
          <Button onClick={confirmModal.open}>Show Confirm Modal</Button>
          <Button onClick={() => formModal.open({ name: 'Test Project' })}>
            Show Form Modal
          </Button>
        </Space>

        <ConfirmModal
          visible={confirmModal.visible}
          onCancel={confirmModal.close}
          onConfirm={() => {
            toast.success('Confirmed!');
            confirmModal.close();
          }}
          title="Delete Item"
          message="Are you sure you want to delete this item? This action cannot be undone."
        />

        <FormModal
          visible={formModal.visible}
          onCancel={formModal.close}
          onSubmit={(values) => {
            toast.success('Form submitted', JSON.stringify(values, null, 2));
          }}
          title="Create Project"
          initialValues={formModal.data}
        >
          <Input.Group>
            <Form.Item
              name="name"
              label="Project Name"
              rules={[{ required: true, message: 'Please enter a name' }]}
            >
              <Input placeholder="Enter project name" />
            </Form.Item>
            <Form.Item name="description" label="Description">
              <Input.TextArea rows={4} placeholder="Enter description" />
            </Form.Item>
          </Input.Group>
        </FormModal>
      </Section>
    </div>
  );
}

/**
 * Buttons Tab
 */
function ButtonsTab() {
  return (
    <div>
      <Section title="Button Types">
        <Space wrap>
          <Button type="primary">Primary</Button>
          <Button>Default</Button>
          <Button type="dashed">Dashed</Button>
          <Button type="text">Text</Button>
          <Button type="link">Link</Button>
          <Button danger>Danger</Button>
        </Space>
      </Section>

      <Section title="Button Sizes">
        <Space wrap>
          <Button size="large">Large</Button>
          <Button>Default</Button>
          <Button size="small">Small</Button>
        </Space>
      </Section>

      <Section title="Icon Buttons with Text">
        <Space wrap>
          <Button type="primary" icon={<PlusOutlined />}>
            Create
          </Button>
          <Button icon={<EditOutlined />}>Edit</Button>
          <Button icon={<DeleteOutlined />} danger>
            Delete
          </Button>
          <Button icon={<DownloadOutlined />}>Export</Button>
          <Button icon={<ReloadOutlined />}>Refresh</Button>
        </Space>
      </Section>

      <Section title="Icon-Only Buttons">
        <Space wrap>
          <Button type="primary" icon={<PlusOutlined />} />
          <Button type="primary" icon={<EditOutlined />} />
          <Button type="primary" icon={<DownloadOutlined />} />
          <Button type="primary" icon={<ReloadOutlined />} />
          <Button icon={<EditOutlined />} />
          <Button icon={<DeleteOutlined />} danger />
          <Button type="primary" icon={<PlusOutlined />} disabled />
        </Space>
      </Section>

      <Section title="Button States">
        <Space wrap direction="vertical">
          <Space wrap>
            <Button type="primary">Normal</Button>
            <Button type="primary" className="ant-btn-hover-effect" style={{ backgroundColor: '#4B3781' }}>
              Hover Preview
            </Button>
            <Button type="primary" disabled>Disabled</Button>
          </Space>
          <Space wrap>
            <Button type="primary" icon={<PlusOutlined />} />
            <Button type="primary" icon={<PlusOutlined />} style={{ backgroundColor: '#4B3781' }}>
              Hover Icon
            </Button>
            <Button type="primary" icon={<PlusOutlined />} disabled />
          </Space>
        </Space>
      </Section>
    </div>
  );
}

/**
 * Forms & Filters Tab
 */
function FormsFiltersTab() {
  const filters = useFilters({ status: null, search: '' });
  const [dateRange, setDateRange] = useState(null);
  const [selectedColumns, setSelectedColumns] = useState(['col1', 'col2']);
  const [searchValue, setSearchValue] = useState('');
  const [statusValue, setStatusValue] = useState(null);
  const [dateValue, setDateValue] = useState(null);

  return (
    <div>
      <Section title="FilterBar">
        <FilterBar onClear={() => toast.success('Filters cleared!')}>
          <StatusSelect value={filters.filters.status} onChange={(v) => filters.setFilter('status', v)} />
          <DateRangePicker value={dateRange} onChange={setDateRange} />
          <SearchInput value={filters.filters.search} onChange={(v) => filters.setFilter('search', v)} />
        </FilterBar>
      </Section>

      <Section title="Form Inputs - Premium Design System">
        <Alert
          type="success"
          message="Modern Label Pattern"
          description={
            <div>
              <p><strong>Labels:</strong> Plural nouns (Columns, Projects, Status)</p>
              <p><strong>Placeholders:</strong> Action verb phrases (Select columns, Search projects)</p>
              <p>This pattern is used by Linear, Vercel, Stripe, Notion, and GitHub</p>
            </div>
          }
          showIcon
          style={{ marginBottom: spacing.lg }}
        />

        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <div style={{ marginBottom: 4 }}>
              <Text strong>Projects</Text>
            </div>
            <SearchInput
              value={searchValue}
              onChange={setSearchValue}
              placeholder="Search projects"
              style={{ width: 300 }}
            />
          </div>
          <div>
            <div style={{ marginBottom: 4 }}>
              <Text strong>Status</Text>
            </div>
            <StatusSelect
              value={statusValue}
              onChange={setStatusValue}
              placeholder="Select status"
              style={{ width: 200 }}
            />
          </div>
          <div>
            <div style={{ marginBottom: 4 }}>
              <Text strong>Columns</Text>
              <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                (Click dropdown → use "Select All" or click items)
              </Text>
            </div>
            <MultiSelect
              value={selectedColumns}
              onChange={setSelectedColumns}
              options={[
                { value: 'col1', label: 'Column 1' },
                { value: 'col2', label: 'Column 2' },
                { value: 'col3', label: 'Column 3' },
                { value: 'col4', label: 'Column 4' },
              ]}
              placeholder="Select columns"
              style={{ width: 300 }}
            />
            <div style={{ marginTop: 8 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Selected: {selectedColumns.join(', ') || 'None'}
              </Text>
            </div>
          </div>
          <div>
            <div style={{ marginBottom: 4 }}>
              <Text strong>Date Range</Text>
            </div>
            <DateRangePicker
              value={dateValue}
              onChange={setDateValue}
              placeholder={['Start date', 'End date']}
              style={{ width: 300 }}
            />
          </div>
        </Space>
      </Section>

      <Section title="Form Section (Collapsible)">
        <FormSection title="Basic Information" collapsible>
          <Input placeholder="Enter project name" />
        </FormSection>
        <FormSection title="Advanced Settings" collapsible defaultCollapsed>
          <Input.TextArea rows={4} placeholder="Enter configuration..." />
        </FormSection>
      </Section>
    </div>
  );
}

/**
 * Loading States Tab
 */
function LoadingStatesTab() {
  const [showError, setShowError] = useState(false);

  return (
    <div>
      <Section title="Loading States">
        <Row gutter={[16, 16]}>
          <Col xs={24} md={8}>
            <Card title="Spinner">
              <LoadingState type="spinner" />
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card title="Skeleton">
              <LoadingState type="skeleton" rows={3} />
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card title="Table Skeleton">
              <LoadingState type="table" rows={3} />
            </Card>
          </Col>
        </Row>
      </Section>

      <Section title="Error States">
        <Button onClick={() => setShowError(!showError)} style={{ marginBottom: 16 }}>
          Toggle Error
        </Button>
        {showError && (
          <ErrorState
            error="Network request failed"
            title="We couldn't load the data"
            description="Please try again"
            onRetry={() => toast.info('Retrying...')}
            onBack={() => setShowError(false)}
          />
        )}
      </Section>

      <Section title="No Data">
        <NoData description="No items found" />
      </Section>
    </div>
  );
}

/**
 * New Components Tab
 */
function NewComponentsTab() {
  const [score, setScore] = useState(75);

  return (
    <div>
      <Section title="Score Gauges">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <ScoreGauge title="Trust Score" score={score} size="large" />
          </Col>
          <Col xs={24} sm={8}>
            <ScoreGauge title="Compliance" score={score} />
          </Col>
          <Col xs={24} sm={8}>
            <ScoreGauge title="Maturity" score={score} size="small" />
          </Col>
        </Row>
        <Slider value={score} onChange={setScore} style={{ marginTop: 16 }} />
      </Section>

      <Section title="Progress Bars">
        <Space direction="vertical" style={{ width: '100%' }}>
          <ProgressBar percent={85} status="pass" label="Pass Rate" />
          <ProgressBar percent={45} status="fail" label="Fail Rate" />
          <ProgressBar percent={65} status="warning" label="Warning Rate" />
          <ProgressBar percent={75} status="info" label="Completion" />
        </Space>
      </Section>

      <Section title="Trend Chart">
        <TrendChart
          data={[
            { value: 65, timestamp: '2024-01-01' },
            { value: 70, timestamp: '2024-01-02' },
            { value: 68, timestamp: '2024-01-03' },
            { value: 75, timestamp: '2024-01-04' },
            { value: 80, timestamp: '2024-01-05' },
          ]}
          color={colors.primary[500]}
          height={60}
        />
      </Section>

      <Section title="Info Tooltip & Breadcrumbs">
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <Text>Hover for info</Text>
            <InfoTooltip content="This is helpful information about this field" />
          </div>
          <Breadcrumbs
            items={[
              { label: 'TrustGrid', path: '/trustgrid' },
              { label: 'Projects', path: '/trustgrid/projects' },
              { label: 'Project Details' },
            ]}
          />
        </Space>
      </Section>
    </div>
  );
}

/**
 * Config System Tab (NEW - Schema-driven control config)
 */
function ConfigSystemTab() {
  const [selectedControl, setSelectedControl] = useState('CTRL_01');
  const [usePlugins, setUsePlugins] = useState(true);

  const controlOptions = [
    { value: 'CTRL_01', label: 'CTRL_01 - PII Detection & Masking', hasPlugin: true },
    { value: 'CTRL_02', label: 'CTRL_02 - Data Quality Monitoring', hasPlugin: true },
    { value: 'CTRL_03', label: 'CTRL_03 - Infrastructure Filtering', hasPlugin: true },
    { value: 'CTRL_04', label: 'CTRL_04 - Explainability & Transparency', hasPlugin: true },
    { value: 'CTRL_05', label: 'CTRL_05 - Training Data Audit Trail', hasPlugin: true },
    { value: 'CTRL_06', label: 'CTRL_06 - Model Performance Monitoring', hasPlugin: true },
    { value: 'CTRL_07', label: 'CTRL_07 - Bias & Fairness Testing', hasPlugin: true },
    { value: 'CTRL_08', label: 'CTRL_08 - Human Oversight & Review', hasPlugin: true },
    { value: 'CTRL_09', label: 'CTRL_09 - Data Residency & Sovereignty', hasPlugin: true },
    { value: 'CTRL_10', label: 'CTRL_10 - Documentation & Model Cards', hasPlugin: true },
    { value: 'CTRL_11', label: 'CTRL_11 - Incident Response & Rollback', hasPlugin: true },
    { value: 'CTRL_12', label: 'CTRL_12 - Purpose Limitation', hasPlugin: true },
    { value: 'CTRL_13', label: 'CTRL_13 - Data Provenance', hasPlugin: true },
    { value: 'CTRL_14', label: 'CTRL_14 - Data Minimization', hasPlugin: true },
  ];

  // Get current schema for display
  const currentSchema = getControlSchema(selectedControl);
  const selectedOption = controlOptions.find((opt) => opt.value === selectedControl);
  const hasPlugin = selectedOption?.hasPlugin;

  return (
    <div>
      <Alert
        type="info"
        message="Schema-Driven Control Configuration System"
        description="GenericConfigPanel renders any control config from schema definition. Select a control to preview. All 14 TrustGrid controls have complete schemas AND custom plugins with enhanced dashboards and visualizations. Changes won't be saved (demo mode)."
        showIcon
        style={{ marginBottom: spacing.lg }}
      />

      <Section title="Select Control to Preview">
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Select
            value={selectedControl}
            onChange={setSelectedControl}
            options={controlOptions.map((opt) => ({
              ...opt,
              label: (
                <span>
                  {opt.label}
                  {opt.hasPlugin && (
                    <Tag color="blue" style={{ marginLeft: 8, fontSize: 10 }}>
                      HAS PLUGIN
                    </Tag>
                  )}
                </span>
              ),
            }))}
            style={{ width: 500 }}
          />

          {hasPlugin && (
            <div>
              <Switch
                checked={usePlugins}
                onChange={setUsePlugins}
                checkedChildren="Plugin Enhanced"
                unCheckedChildren="Schema Only"
              />
              <Text type="secondary" style={{ marginLeft: spacing.md, fontSize: 13 }}>
                {usePlugins
                  ? 'Showing enhanced plugin UI with visualizations'
                  : 'Showing standard schema-driven UI'}
              </Text>
            </div>
          )}

          {!hasPlugin && (
            <Alert
              message="Schema-Only Mode"
              description="All 14 controls now have custom plugins. Toggle 'Plugin Enhanced' to see the dashboard visualizations."
              type="info"
              showIcon
              style={{ marginTop: spacing.sm }}
            />
          )}
        </Space>
      </Section>

      <Section title={`${selectedControl} Configuration Panel`}>
        <GenericConfigPanel
          projectId="demo-project"
          controlCode={selectedControl}
          usePlugin={usePlugins}
        />
      </Section>

      <Section title={
        <span>
          <CodeOutlined style={{ marginRight: 8, color: colors.primary[500] }} />
          Schema Definition & Plugin System
        </span>
      }>
        <Alert
          type="success"
          message="How It Works"
          description={
            <div>
              <p><strong>Schema-Driven System:</strong></p>
              <p>1. Each control has a schema in <code>controlSchemas.js</code></p>
              <p>2. Schema defines sections, fields, types, validation, defaults</p>
              <p>3. GenericConfigPanel renders from schema (no hardcoded UI)</p>
              <p>4. Supports 8 field types: text, number, select, multiselect, checkbox, slider, radio, tags</p>
              <p>5. All 14 controls (CTRL_01 through CTRL_14) have complete schemas</p>
              <p></p>
              <p><strong>Plugin Enhancement System:</strong></p>
              <p>6. All 14 controls have custom plugins in <code>config/plugins/</code></p>
              <p>7. Plugins extend GenericConfigPanel with dashboards, visualizations, custom UI</p>
              <p>8. CTRL_01: PII Detection - Type cards with detection stats</p>
              <p>9. CTRL_02: Data Quality - Quality metrics dashboard</p>
              <p>10. CTRL_03: Infrastructure Filter - Filter statistics and events</p>
              <p>11. CTRL_04: Explainability - Method cards (SHAP, LIME, etc.)</p>
              <p>12. CTRL_05: Training Audit - Audit log viewer with lineage</p>
              <p>13. CTRL_06: Performance - Metric cards (accuracy, latency, etc.)</p>
              <p>14. CTRL_07: Fairness - Bias metrics and visualizations</p>
              <p>15. CTRL_08: Human Oversight - Review queue dashboard</p>
              <p>16. CTRL_09: Data Residency - Compliance tracking by region</p>
              <p>17. CTRL_10: Documentation - Completeness checklist</p>
              <p>18. CTRL_11: Incident Response - Incident tracking dashboard</p>
              <p>19. CTRL_12: Purpose Limitation - Usage monitoring</p>
              <p>20. CTRL_13: Data Provenance - Lineage graph and source tracking</p>
              <p>21. CTRL_14: Data Minimization - Field analysis and redundancy detection</p>
              <p>22. Plugins are optional - schemas work standalone</p>
            </div>
          }
          showIcon
        />
        <Card
          title={<Text strong>Current Schema ({selectedControl})</Text>}
          style={{ marginTop: spacing.md }}
          bodyStyle={{ padding: 12 }}
        >
          <pre style={{
            fontFamily: 'Monaco, Courier New, monospace',
            fontSize: 11,
            lineHeight: 1.5,
            margin: 0,
            color: '#17052B',
            backgroundColor: '#F7F5FC',
            padding: 12,
            borderRadius: 6,
            overflow: 'auto',
            maxHeight: 400
          }}>
            {currentSchema && JSON.stringify(currentSchema, null, 2)}
          </pre>
        </Card>
      </Section>
    </div>
  );
}

/**
 * Main Component Showcase
 */
function ComponentShowcase() {
  const tabItems = [
    {
      key: 'theme',
      label: 'Theme Tokens',
      children: <ThemeTokensTab />,
    },
    {
      key: 'layout',
      label: 'Layout',
      children: <LayoutComponentsTab />,
    },
    {
      key: 'buttons',
      label: 'Buttons',
      children: <ButtonsTab />,
    },
    {
      key: 'forms',
      label: 'Forms & Filters',
      children: <FormsFiltersTab />,
    },
    {
      key: 'datagrid',
      label: 'Advanced Data Grid',
      children: <DataGridTab />,
    },
    {
      key: 'config',
      label: 'Config System',
      children: <ConfigSystemTab />,
    },
    {
      key: 'tags',
      label: 'Tags & Badges',
      children: <TagsTab />,
    },
    {
      key: 'scores',
      label: 'Scores',
      children: <ScoresTab />,
    },
    {
      key: 'new',
      label: 'New Components',
      children: <NewComponentsTab />,
    },
    {
      key: 'loading',
      label: 'Loading States',
      children: <LoadingStatesTab />,
    },
    {
      key: 'cards',
      label: 'Cards',
      children: <CardsTab />,
    },
    {
      key: 'tables',
      label: 'Tables',
      children: <TablesTab />,
    },
    {
      key: 'modals',
      label: 'Modals',
      children: <ModalsTab />,
    },
  ];

  return (
    <TrustGridPage
      title="Component Showcase"
      subtitle="Interactive catalog of all TrustGrid framework components and Truvex brand"
      breadcrumbs={[{ label: 'Showcase' }]}
    >
      <Alert
        type="info"
        message="Truvex TrustGrid Framework"
        description="Interactive showcase of all components with Truvex branding (#17052B). Test theme changes, components, and interactions here before using in production."
        showIcon
        closable
        style={{ marginBottom: spacing.lg }}
      />

      <Tabs items={tabItems} type="card" />
    </TrustGridPage>
  );
}

export default ComponentShowcase;
